import React, { useEffect, useState, useMemo } from "react";
import { Link, useHistory } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Calendar } from "primereact/calendar";
import OrderService from "../../../../../../service/OrderService";
import localeText from "../../../../../../locale";
import { formatDate } from "../../../../../../utilities/formatDate";
import { formatPrice } from "../../../../../../utilities/formatPrice";
import "./FinancialTransactionsTable.css";

const orderService = new OrderService();

const paymentMethodApiMap = {
  all: null,
  paymentMethodPayPal: "PAYPAL",
  paymentMethodWallet: "WALLET",
  paymentMethodExtracredit: "EXTRACREDIT",
  paymentMethodBankTransfer: "BANK",
  paymentMethodMultiple: "MULTIPLE",
};

const formatDateToISO = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

function getPaymentMethodKey(order) {
  const extracoin = Number(order.amountWalletExtracoin ?? 0) || 0;
  const wallet = Number(order.amountWallet ?? 0) || 0;
  const cash = Number(order.amountCash ?? 0) || 0;
  const bank = Number(order.amountBankTransfer ?? 0) || 0;
  const count = [extracoin, wallet, cash, bank].filter((n) => n > 0).length;
  if (count === 0) return null;
  if (count >= 2) return "paymentMethodMultiple";
  if (extracoin > 0) return "paymentMethodExtracredit";
  if (wallet > 0) return "paymentMethodWallet";
  if (cash > 0) return "paymentMethodPayPal";
  if (bank > 0) return "paymentMethodBankTransfer";
  return null;
}

const mapOrderToTransaction = (order, currencySymbol, loggedUsername) => {
  const value = order.amountTotal ?? order.amountOrder ?? 0;
  let statusKey = "pending";
  if (order.status === "RECEIVED") statusKey = "completed";
  else if (order.status === "CANCELED") statusKey = "canceled";
  else statusKey = "pending";
  const buyerUsername = order?.buyer?.username;
  const sellerUsername = order?.seller?.username;
  const isPurchase = buyerUsername && loggedUsername && buyerUsername === loggedUsername;
  const isSale = sellerUsername && loggedUsername && sellerUsername === loggedUsername;
  let description = localeText("saleInStore") || "Venda de produto na loja";
  if (isPurchase) description = localeText("purchaseInStore") || "Compra de produto na loja";
  else if (isSale) description = localeText("saleInStore") || "Venda de produto na loja";

  return {
    id: order.id,
    description,
    paymentMethodKey: getPaymentMethodKey(order),
    value,
    status: order.status,
    statusKey,
    date: order.date,
    _raw: order,
  };
};

const FinancialTransactionsTable = ({ currencySymbol, hasBankAccounts, plan: userPlan }) => {
  const history = useHistory();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [dismissBanner] = useState(false);
  const [filterPaymentMethod, setFilterPaymentMethod] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [showDateFilterDialog, setShowDateFilterDialog] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [draftStartDate, setDraftStartDate] = useState(null);
  const [draftEndDate, setDraftEndDate] = useState(null);
  const loggedUsername = localStorage.getItem("__extraconomy_username");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const token = localStorage.getItem("__extraconomy_token");
        const params = {
          page,
          pageSize,
        };
        const paymentMethod = paymentMethodApiMap[filterPaymentMethod];
        if (paymentMethod) params.paymentMethod = paymentMethod;
        if (debouncedSearchText) params.q = debouncedSearchText;
        const startDateISO = formatDateToISO(startDate);
        const endDateISO = formatDateToISO(endDate);
        if (startDateISO) params.startDate = startDateISO;
        if (endDateISO) params.endDate = endDateISO;

        const res = await orderService.getAllTransactions(token, params);
        if (cancelled) return;
        if (res.messageCode === 200000 || res.messageCode === 200001) {
          const pageResult = res?.result ?? {};
          const content = Array.isArray(pageResult.content) ? pageResult.content : [];
          setOrders(content);
          setTotalElements(Number(pageResult.totalElements ?? 0) || 0);
        } else {
          setOrders([]);
          setTotalElements(0);
        }
      } catch {
        if (!cancelled) {
          setOrders([]);
          setTotalElements(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, filterPaymentMethod, debouncedSearchText, startDate, endDate]);

  const transactions = useMemo(
    () => orders.map((o) => mapOrderToTransaction(o, currencySymbol, loggedUsername)),
    [orders, currencySymbol, loggedUsername],
  );

  const showBankBanner = !hasBankAccounts && !dismissBanner;

  const statusConfig = {
    completed: { text: "completed", color: "var(--theme-primary, #1DAF61)" },
    pending: { text: "pending", color: "#F7B005" },
    canceled: { text: "canceled", color: "#eb4034" },
  };

  if (userPlan === "Promoter") {
    return (
      <div className="card border-1 border-300">
        <div className="text-center py-8">
          <i className="pi pi-lock text-8xl text-400 my-4" />
          <h2 className="my-6">{localeText("planNotAllowed")}</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="financial-transactions-table">
      {showBankBanner && (
        <div
          className="card border-round-md p-3 mb-4 flex align-items-center justify-content-between"
          style={{
            backgroundColor: "rgba(249, 168, 37, 0.2)",
            border: "none",
            borderLeft: "4px solid rgba(249, 168, 37, 1)",
          }}
          role="alert"
        >
          <p className="m-0" style={{ color: "var(--theme-text, #171717)" }}>
            {localeText("noBankAccountWarning")}
          </p>
          <Link
            to="/dashboard-financeiro/carteira?tab=contas-bancarias"
            className="p-button p-button-text p-button-sm underline"
            style={{ color: "var(--theme-text, #171717)" }}
            aria-label={localeText("bankAccountSettingsLink")}
          >
            {localeText("bankAccountSettingsLink")}
          </Link>
        </div>
      )}

      <div className="flex flex-wrap align-items-center gap-2 mb-3">
        <div
          className="flex gap-1 flex-wrap p-1"
          style={{
            backgroundColor: "color-mix(in srgb, var(--theme-border) 35%, transparent)",
            borderRadius: "0.625rem",
          }}
        >
          <Button
            size="small"
            label={localeText("all")}
            style={{ color: "#A3A3A3" }}
            className={`p-button-text border-round-pill font-medium ${filterPaymentMethod === "all" ? "filter-pill-active" : ""} p-no-outline`}
            onClick={() => setFilterPaymentMethod("all")}
          />
          <Button
            size="small"
            label={localeText("paymentMethodPayPal")}
            style={{ color: "#A3A3A3" }}
            className={`p-button-text border-round-pill font-medium ${filterPaymentMethod === "paymentMethodPayPal" ? "filter-pill-active" : ""} p-no-outline`}
            onClick={() => setFilterPaymentMethod("paymentMethodPayPal")}
          />
          <Button
            size="small"
            label={localeText("paymentMethodWallet")}
            style={{ color: "#A3A3A3" }}
            className={`p-button-text border-round-pill font-medium ${filterPaymentMethod === "paymentMethodWallet" ? "filter-pill-active" : ""} p-no-outline`}
            onClick={() => setFilterPaymentMethod("paymentMethodWallet")}
          />
          <Button
            size="small"
            label={localeText("paymentMethodExtracredit")}
            style={{ color: "#A3A3A3" }}
            className={`p-button-text border-round-pill font-medium ${filterPaymentMethod === "paymentMethodExtracredit" ? "filter-pill-active" : ""} p-no-outline`}
            onClick={() => setFilterPaymentMethod("paymentMethodExtracredit")}
          />
          <Button
            size="small"
            label={localeText("paymentMethodBankTransfer")}
            style={{ color: "#A3A3A3" }}
            className={`p-button-text border-round-pill font-medium ${filterPaymentMethod === "paymentMethodBankTransfer" ? "filter-pill-active" : ""} p-no-outline`}
            onClick={() => setFilterPaymentMethod("paymentMethodBankTransfer")}
          />
        </div>
        <span className="p-input-icon-left flex-1" style={{ minWidth: "200px" }}>
          <i className="pi pi-search" />
          <InputText
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={localeText("searchByTransaction")}
            className="w-full border-round-lg"
          />
        </span>
        <Button
          label={localeText("filter")}
          icon="pi pi-sliders-h"
          className="p-button-outlined border-round-lg p-no-outline"
          style={{
            color: "var(--theme-text-muted, #5C5C5C)",
            borderColor: "var(--theme-border, #EBEBEB)",
          }}
          onClick={() => {
            setDraftStartDate(startDate);
            setDraftEndDate(endDate);
            setShowDateFilterDialog(true);
          }}
        />
      </div>

      <Dialog
        header={localeText("filter") || "Filtrar"}
        visible={showDateFilterDialog}
        onHide={() => setShowDateFilterDialog(false)}
        className="w-25rem m-2"
        draggable={false}
      >
        <div className="flex flex-column gap-3">
          <div className="flex flex-column gap-1">
            <label>{localeText("startDate") || "Data inicial"}</label>
            <Calendar
              value={draftStartDate}
              onChange={(e) => setDraftStartDate(e.value)}
              dateFormat="dd/mm/yy"
              showIcon
            />
          </div>
          <div className="flex flex-column gap-1">
            <label>{localeText("endDate") || "Data final"}</label>
            <Calendar
              value={draftEndDate}
              onChange={(e) => setDraftEndDate(e.value)}
              dateFormat="dd/mm/yy"
              showIcon
            />
          </div>
          <div className="flex justify-content-end gap-2 mt-2">
            <Button
              type="button"
              className="p-button-text"
              style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
              label={localeText("clean") || "Limpar"}
              onClick={() => {
                setDraftStartDate(null);
                setDraftEndDate(null);
                setStartDate(null);
                setEndDate(null);
                setPage(1);
                setShowDateFilterDialog(false);
              }}
            />
            <Button
              type="button"
              label={localeText("applyChanges") || localeText("filter") || "Aplicar"}
              className="border-round-md p-no-outline"
              onClick={() => {
                setStartDate(draftStartDate);
                setEndDate(draftEndDate);
                setPage(1);
                setShowDateFilterDialog(false);
              }}
            />
          </div>
        </div>
      </Dialog>

      <div className="overflow-hidden">
        <DataTable
          value={transactions}
          loading={loading}
          paginator
          lazy
          first={(page - 1) * pageSize}
          rows={pageSize}
          totalRecords={totalElements}
          rowsPerPageOptions={[10, 20, 50]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
          currentPageReportTemplate=""
          onPage={(e) => {
            setPage((e.page ?? 0) + 1);
            setPageSize(e.rows ?? 10);
          }}
          emptyMessage={
            <div className="flex flex-column align-items-center justify-content-center py-8 gap-3">
              <div
                className="border-round flex align-items-center justify-content-center"
                style={{
                  width: "120px",
                  height: "120px",
                  backgroundColor: "color-mix(in srgb, var(--theme-border) 35%, transparent)",
                }}
              >
                <i className="pi pi-inbox text-6xl text-400" />
              </div>
              <p className="m-0" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
                {localeText("noOperationsRecords")}
              </p>
            </div>
          }
          onRowClick={(e) => {
            if (e.data?._raw?.id) {
              const username = localStorage.getItem("__extraconomy_username");
              const isSeller = e.data?._raw?.seller?.username === username;
              const path = isSeller ? "/dashboard-salesdetails" : "/dashboard-purchasesdetails";
              history.push(`${path}?orderId=${e.data._raw.id}`);
            }
          }}
          rowClassName="cursor-pointer"
          className="financial-transactions-datatable"
        >
          <Column
            field="id"
            header={localeText("transaction")}
            sortable
            body={(row) => <span className="font-medium">{row.id}</span>}
          />
          <Column field="description" header={localeText("description")} sortable />
          <Column
            field="paymentMethodKey"
            header={localeText("operationType")}
            body={(row) => {
              const paymentMethodColors = {
                paymentMethodExtracredit: "#3b82f6",
                paymentMethodWallet: "#10b981",
                paymentMethodPayPal: "#ffc107",
                paymentMethodBankTransfer: "#6366f1",
                paymentMethodMultiple: "#f87171",
              };
              const paymentMethodBgColors = {
                paymentMethodExtracredit: "#E6F3FF",
                paymentMethodWallet: "#ECFDF5",
                paymentMethodPayPal: "#FFF9DB",
                paymentMethodBankTransfer: "#EFF6FF",
                paymentMethodMultiple: "#FEE2E2",
              };
              const color = paymentMethodColors[row.paymentMethodKey] || "#171717";
              const bgColor = paymentMethodBgColors[row.paymentMethodKey] || "#F7F7F7";

              return (
                <span
                  style={{
                    fontWeight: 500,
                    color: color,
                    backgroundColor: bgColor,
                    padding: "0.25rem 0.5rem",
                    borderRadius: "0.25rem",
                  }}
                >
                  {row.paymentMethodKey ? localeText(row.paymentMethodKey) : "—"}
                </span>
              );
            }}
          />
          <Column
            field="value"
            header={localeText("value")}
            sortable
            body={(row) => (
              <span
                style={{
                  fontWeight: 500,
                }}
              >
                {row.value >= 0 ? "+" : ""}
                {currencySymbol} {formatPrice(Math.abs(row.value))}
              </span>
            )}
          />
          <Column
            field="status"
            header={localeText("status")}
            body={(row) => {
              const cfg = statusConfig[row.statusKey] || statusConfig.pending;
              return (
                <span className="flex align-items-center gap-2">
                  <span
                    className="border-circle"
                    style={{
                      width: "8px",
                      height: "8px",
                      backgroundColor: cfg.color,
                    }}
                  />
                  {localeText(cfg.text)}
                </span>
              );
            }}
          />
          <Column
            field="date"
            header={localeText("registrationDate")}
            sortable
            body={(row) => formatDate(row.date)}
          />
          <Column
            header=""
            body={() => <i className="pi pi-chevron-right text-400" />}
            style={{ width: "40px" }}
          />
        </DataTable>
      </div>
    </div>
  );
};

export default FinancialTransactionsTable;
